import { useForm } from 'react-hook-form';
import { Form, FormGroup } from 'semantic-ui-react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { Check, Info } from '@phosphor-icons/react';
import styled from 'styled-components';
import { DarkPopup, SkupremeModal } from '../../components';
import {
  ControlledTextFormField,
  DropdownFormField,
  HoverBorderButton,
  MuteText,
  NumberFormField,
  StyledButton,
} from '../../elements';
import {
  CommonUtility,
  PackagingMaterialService,
  packagingMaterialTypes,
  RackService,
  theme,
  ToastMessage,
} from '../../utility';
import { useDebouncedEffect } from '../../hooks';
import { ConversionUtility } from '../../utility/conversions';
import { MetricImperialToggle } from '../../components/MetricImperialToggle';

const Icon = styled.div`
  position: absolute;
  left: 10px;
  top: 9.5px;
  color: ${({ theme }) => theme.colors.grey};
  pointer-events: none;
`;

const IconNumberFormField = styled(NumberFormField)`
  input {
    padding-left: 20px !important;
  }
`;

const PackagingSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
  packagingType: yup.string().required('Name is required'),
  length: yup
    .number()
    .required('Length is required')
    .moreThan(0, 'Length is required')
    .typeError('Length is required'),
  width: yup
    .number()
    .required('Width is required')
    .moreThan(0, 'Width is required')
    .typeError('Width is required'),
  height: yup
    .number()
    .required('Height is required')
    .moreThan(0, 'Height is required')
    .typeError('Height is required'),
  weight: yup
    .number()
    .nullable()
    .transform((_, val) => (!isNaN(val) ? Number(val) : null))
    .typeError('Weight should be a number'),
  useCase: yup.string().optional(),
  locationId: yup.string().required('Inventory Location is required'),
  unitsAvailable: yup.string().optional(''),
  price: yup.string().optional(''),
});

const decimalMask = {
  allowDecimal: true,
  allowLeadingZeroes: false,
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ',',
};

const locationStatuses = {
  pending: 'pending',
  error: 'error',
  success: 'success',
};

const attributeSchema = {
  height: '',
  length: '',
  width: '',
  weight: '',
};

export function AddPackageMaterial({
  warehouseDetail,
  open,
  onClose,
  warehouseId,
  refresh,
  selectedItem,
}) {
  const isEditeMode = CommonUtility.isValidObject(selectedItem);
  const [loading, setLoading] = useState(false);

  const [codeLoading, setCodeLoading] = useState(false);
  const [locationStatus, setLocationStatus] = useState('');
  const [locationId, setLocationId] = useState('');

  const [isImperial, setImperialForm] = useState(false);

  const {
    formState: { errors },
    control,
    handleSubmit,
    watch,
    reset,
    getValues,
  } = useForm({
    resolver: yupResolver(PackagingSchema),
  });

  const changeUnits = () => {
    const watchFields = getValues();
    let newItem = null;

    if (isImperial) {
      newItem = convertFieldsToMetric(watchFields);
    } else {
      newItem = convertFieldsToImperial(watchFields);
    }

    reset({
      ...watchFields,
      ...newItem,
    });

    setImperialForm(!isImperial);
  };

  const convertFieldsToMetric = watchFields => {
    const item = attributeSchema;

    Object.keys(attributeSchema).forEach(key => {
      if (['weight'].includes(key)) {
        item[key] = ConversionUtility.poundToKilo(watchFields[key]);
      } else {
        item[key] = ConversionUtility.inchToCenti(watchFields[key]);
      }
    });

    return item;
  };

  const convertFieldsToImperial = watchFields => {
    const item = attributeSchema;

    Object.keys(attributeSchema).forEach(key => {
      if (['weight'].includes(key)) {
        item[key] = ConversionUtility.kiloToPound(watchFields[key]);
      } else {
        item[key] = ConversionUtility.centiToInch(watchFields[key]);
      }
    });

    return item;
  };

  useDebouncedEffect(
    () => {
      if (CommonUtility.isValidObject(selectedItem)) {
        resetUsingItem();
        setImperialForm(selectedItem?.dimensions?.uom === 'in');
      } else {
        setImperialForm(warehouseDetail?.dimensions?.measurementUnit === 'ft');
        reset({
          name: '',
          packagingType: '',
          weight: '',
          width: '',
          height: '',
          length: '',
          useCase: '',
          locationId: '',
          unitsAvailable: '',
          price: '',
        });
      }
    },
    [selectedItem, open],
    50,
  );

  const resetUsingItem = () => {
    reset({
      ...selectedItem,
      ...(selectedItem.dimensions || {}),
      price: selectedItem.price.value,
      weight: selectedItem.weight.value,
      locationId: selectedItem.locationId.code,
    });
  };

  const locationCode = watch('locationId');

  const onSubmit = async formValue => {
    if (codeLoading || loading) {
      return;
    }

    let locationToUse = locationId;

    if (locationStatus !== locationStatuses.success) {
      const verifiedLocationId = await verifyLocation(formValue.locationId);
      if (verifiedLocationId) {
        locationToUse = verifiedLocationId;
      } else {
        return;
      }
    }

    try {
      setLoading(true);
      const payload = {
        warehouseId: selectedItem?.warehouseId || warehouseId,
        name: formValue.name,
        packagingType: formValue.packagingType,
        dimensions: {
          length: formValue.length,
          width: formValue.width,
          height: formValue.height,
          uom: isImperial ? 'in' : 'cm',
        },
        weight: {
          value: formValue.weight,
          uom: isImperial ? 'lbs' : 'kg',
        },
        useCase: formValue.useCase,
        locationId: locationToUse,
        unitsAvailable: formValue.unitsAvailable || 0,
        price: {
          value: formValue.price || 0,
          currency: 'USD',
        },
      };

      if (isEditeMode) {
        await PackagingMaterialService.update(selectedItem?._id, payload);
      } else {
        await PackagingMaterialService.add(payload);
      }
      onClose();
      refresh();
      ToastMessage.success('New packing material added successfully');
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (locationStatus !== locationStatuses.pending) {
      setLocationStatus(locationStatuses.pending);
    }
  }, [locationCode]);

  const verifyLocation = async code => {
    if (!code) {
      ToastMessage.error('Please enter location code');
      return;
    }

    let locationId = null;

    try {
      setCodeLoading(true);
      const params = { warehouseId };
      const res = await RackService.verifyLocation(code, params);
      if (res?.exists) {
        locationId = res.location?.id;
        setLocationStatus(locationStatuses.success);
        setLocationId(res.location?.id);
      } else {
        setLocationId('');
        setLocationStatus(locationStatuses.error);
      }
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setCodeLoading(false);
    }
    return locationId;
  };

  return (
    <SkupremeModal
      size="tiny"
      open={open}
      onClose={onClose}
      title={isEditeMode ? 'Edit Packaging Material' : 'New Packaging Material'}
    >
      <MetricImperialToggle
        toggle
        onChange={() => changeUnits()}
        checked={isImperial}
        className="mx-3"
        wrapperClassName="mt-2 mb-3"
      />
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ControlledTextFormField
          name="name"
          label="Name"
          control={control}
          error={errors?.name}
          hint={errors?.name?.message}
        />
        <DropdownFormField
          name="packagingType"
          label="Packaging Type"
          control={control}
          error={errors?.packagingType}
          hint={errors?.packagingType?.message}
          options={packagingMaterialTypes}
        />
        <MuteText>Size ({isImperial ? 'in' : 'cm'})</MuteText>
        <FormGroup className="mb-0">
          <NumberFormField
            name="length"
            placeholder="L"
            control={control}
            error={errors?.length}
            hint={errors?.length?.message}
            maskOptions={decimalMask}
          />
          <MuteText className="mt-2 mx-1">x</MuteText>
          <NumberFormField
            name="width"
            placeholder="W"
            control={control}
            error={errors?.width}
            hint={errors?.width?.message}
            maskOptions={decimalMask}
          />
          <MuteText className="mt-2 mx-1">x</MuteText>
          <NumberFormField
            name="height"
            placeholder="H"
            control={control}
            error={errors?.height}
            hint={errors?.height?.message}
            maskOptions={decimalMask}
          />
        </FormGroup>
        <NumberFormField
          name="weight"
          label={`Weight (${isImperial ? 'lb' : 'kg'})`}
          control={control}
          error={errors?.weight}
          hint={errors?.weight?.message}
          maskOptions={decimalMask}
        />
        <FormGroup className="mb-0" widths="equal">
          <ControlledTextFormField
            name="useCase"
            label="Use Case"
            control={control}
            error={errors?.useCase}
            hint={errors?.useCase?.message}
            fieldClass="mb-0"
          />
          <ControlledTextFormField
            name="locationId"
            placeholder="Inventory Location"
            label={
              <div className="d-flex align-items-center">
                <span>Inventory Location</span>
                {[locationStatuses.pending, locationStatuses.success].includes(
                  locationStatus,
                ) && (
                  <DarkPopup
                    trigger={
                      locationStatus === locationStatuses.success ? (
                        <Check className="mb-1 ml-1" color={theme.colors.green} />
                      ) : (
                        <Info className="mb-1 ml-1" color={theme.colors.orange} />
                      )
                    }
                  >
                    {locationStatus === locationStatuses.pending
                      ? 'Not Verified'
                      : 'Verified'}
                  </DarkPopup>
                )}
              </div>
            }
            control={control}
            error={errors?.locationId || locationStatus === locationStatuses.error}
            hint={
              locationStatus === locationStatuses.error
                ? 'Location not found'
                : errors?.locationId?.message
            }
            fieldClass="mb-0"
          />
        </FormGroup>
        <div className="d-flex justify-content-end mt-2">
          <HoverBorderButton
            type="button"
            size="mini"
            loading={codeLoading}
            disabled={loading}
            onClick={() => verifyLocation(locationCode)}
          >
            Verify
          </HoverBorderButton>
        </div>
        <NumberFormField
          name="unitsAvailable"
          label="Units Available"
          control={control}
          error={errors?.unitsAvailable}
          hint={errors?.unitsAvailable?.message}
        />
        <IconNumberFormField
          name="price"
          label="Price"
          control={control}
          error={errors?.price}
          hint={errors?.price?.message}
          maskOptions={decimalMask}
          inputLeft={<Icon className="icon">$</Icon>}
        />
        <div className="d-flex justify-content-end">
          <HoverBorderButton
            disabled={loading || codeLoading}
            type="button"
            onClick={onClose}
          >
            Cancel
          </HoverBorderButton>
          <StyledButton
            disabled={codeLoading}
            loading={loading}
            type="submit"
            className="ml-2"
          >
            {isEditeMode ? 'Update' : 'Create'}
          </StyledButton>
        </div>
      </Form>
    </SkupremeModal>
  );
}
